import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { Eyebrow } from './Eyebrow';

import { cn } from '@/lib/utils';

export function Hero({
  eyebrow,
  title,
  description,
  image,
  align = 'left',
  children,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  /** Background image URL — rendered behind a surface-hero gradient overlay. */
  image: string;
  /** `left` = Home's wide, left-aligned treatment. `center` = About's narrower, centered one. */
  align?: 'left' | 'center';
  /** CTA buttons or other content rendered below the description. */
  children?: ReactNode;
}): JSX.Element {
  const centered = align === 'center';

  return (
    <section className="relative overflow-hidden bg-surface-hero text-white">
      <motion.div
        className={cn('absolute inset-0 bg-cover bg-center', centered && 'opacity-60')}
        style={{ backgroundImage: `url('${image}')` }}
        initial={{ scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-t from-surface-hero',
          centered
            ? 'via-surface-hero/85 to-surface-hero/50'
            : 'via-surface-hero/80 to-surface-hero/40',
        )}
      />
      <div
        className={cn(
          'relative mx-auto px-6 flex flex-col gap-6',
          centered
            ? 'max-w-4xl py-24 sm:py-32 items-center text-center'
            : 'max-w-6xl py-28 sm:py-36 items-start',
        )}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Eyebrow tone="inverted" center={centered}>
            {eyebrow}
          </Eyebrow>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className={cn(
            'font-display font-semibold leading-[1.05] text-white',
            centered
              ? 'text-5xl sm:text-6xl max-w-3xl'
              : 'text-5xl sm:text-6xl lg:text-7xl max-w-3xl',
          )}
        >
          {title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={cn('text-lg sm:text-xl text-white/75', centered ? 'max-w-2xl' : 'max-w-xl')}
        >
          {description}
        </motion.p>
        {children && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className={cn('flex flex-wrap gap-3 mt-2', centered && 'justify-center')}
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
}
